import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LoggerService } from './logger.service'; // ggf. Pfad anpassen

export interface ActiveFaces {
  home: boolean;
  about: boolean;
  resume: boolean;
  portfolio: boolean;
  blog: boolean;
  contact: boolean;
}

const INITIAL_ACTIVE_FACES: ActiveFaces = {
  home: true,
  about: true,
  resume: true,
  portfolio: true,
  blog: false,
  contact: false
};

@Injectable({ providedIn: 'root' })
export class CubeService {

  constructor(private logger: LoggerService) {}

  // CSS-Transition-Dauer (immer 2s)
  private readonly rotationDuration = 2000; // ms

  // Rotation in Grad (Wert, der per Data-Binding an die .box übergeben wird)
  private rotationY = 0;

  // Numerischer Index für die aktuelle Seite:
  // 0: Home, 1: About, 2: Resume, 3: Portfolio, 4: Blog, 5: Contact
  private currentIndex = 0;

  // Wir verwalten den Active-State als Objekt, sodass mehrere Seiten gleichzeitig aktiv sein können.
  private _activeFaces = new BehaviorSubject<ActiveFaces>(INITIAL_ACTIVE_FACES);
  activeFaces$ = this._activeFaces.asObservable();

  private _currentIndex$ = new BehaviorSubject<number>(this.currentIndex);
  public currentIndex$ = this._currentIndex$.asObservable();

  private _isLocked = new BehaviorSubject<boolean>(false);
  public isLocked$ = this._isLocked.asObservable();

  public lockCube() {
    this._isLocked.next(true);
  }

  public unlockCube() {
    this._isLocked.next(false);
  }

  // Hilfsfunktion: Aktualisiert den ActiveState (wir erstellen eine Kopie)
  private updateActiveFaces(newState: ActiveFaces) {
    console.log(this._activeFaces);

    console.log(newState);
    this._activeFaces.next({ ...newState });
  }

  // Getter: Rotationswinkel (für die Template-Bindung)
  public getRotationY(): number {
    return this.rotationY;
  }

  // Getter: aktueller numerischer Index
  public get current(): number {
    return this.currentIndex;
  }

  private enableFaceOnlyFlag(prev: ActiveFaces, targetIndex: number): ActiveFaces {
  const keys: (keyof ActiveFaces)[] = ['home','about','resume','portfolio','blog','contact'];
  const targetKey = keys[targetIndex];
  return { ...prev, [targetKey]: true };
}

  setActiveFace(targetIndex: number): void {
    this.logger.debug('[CubeService] setActiveFace is starting...');
    console.log(this._activeFaces);

    // this.lockCube();

    // Basisprüfungen
    if (targetIndex < 0 || targetIndex > 5) return;
    if (targetIndex === this.currentIndex) return;

    const oldIndex = this.currentIndex;
    const diff = targetIndex - oldIndex;
    const steps = Math.abs(diff); // z. B. 5 für 0→5
    if (steps === 0) return;

    this.logger.debug('[CubeService] oldIndex: ', oldIndex);
    this.logger.debug('[CubeService] targetIndex: ', targetIndex);

    // Bestimme Rotationsrichtung: Wenn targetIndex < oldIndex, dann drehen wir in positive Richtung.
    let direction = -1;
    if (targetIndex < oldIndex) {
      direction = 1;
      this.logger.debug('[CubeService] direction', direction, '(Rotation to left)');
    } else {
      this.logger.debug('[CubeService] direction', direction, '(Rotation to right)');
    }

    const degrees = steps * 90 * direction;

    // Physische Rotation in einem Schritt (CSS-Transition dauert 2s)
    // this.rotationY += degrees;
    this.animateRotation(degrees);
    this.currentIndex = targetIndex;
    this._currentIndex$.next(targetIndex);

    // Standardmäßig schalten wir sofort um – außer wir haben einen Sonderfall.
    let immediateSwitch = true;

    // Logging
    this.logger.debug('[CubeService] steps', steps);
    this.logger.debug(`[CubeService] Rotating from ${oldIndex} to ${targetIndex}: ${degrees}°`);

    // Sonderfall C: Wechsel zwischen Overlays 4 und 5
    if ((oldIndex === 4 && targetIndex === 5) || (oldIndex === 5 && targetIndex === 4)) {
      immediateSwitch = false;
      this.logger.debug(`[CubeService] Sonderfall: Wechsel zwischen Overlays ${oldIndex} -> ${targetIndex}`);
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: false,
        blog: true,
        contact: true
      });
    }
    // Sonderfall A: 0 → 4 (Home → Blog)
    else if (oldIndex === 0 && targetIndex === 4) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: true,
        about: true,
        resume: false,
        portfolio: false,
        blog: false,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 500;
      setTimeout(() => {
        this.logger.debug(`[CubeService] 0→4: After ${partialTime} ms, switching Home→Blog`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 0→4: Final state confirmed as Blog (4)`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: false
        });
      }, this.rotationDuration);
    }
    // Sonderfall D: 1 → 5 (About → Contact)
    else if (oldIndex === 1 && targetIndex === 5) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: true,
        resume: false,
        portfolio: false,
        blog: false,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 900;
      setTimeout(() => {
        this.logger.debug(`[CubeService] 1→5: After ${partialTime} ms, switching About→Contact`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 1→5: Final state confirmed as Contact (5)`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, this.rotationDuration);
    }
    // Sonderfall B: 0 → 5 (Home → Contact)
    else if (oldIndex === 0 && targetIndex === 5) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: true,
        about: true,
        resume: false,
        portfolio: false,
        blog: false,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 500;
      setTimeout(() => {
        this.logger.debug(`[CubeService] 0→5: After ${partialTime} ms, switching Home→Blog & About→Contact`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 0→5: Final state confirmed as Contact (5)`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, this.rotationDuration);
    }

    /* 5 nach 0 */
    else if (oldIndex === 5 && targetIndex === 0) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: false,
        blog: true,
        contact: true
      });
      const partialTime = (this.rotationDuration / steps) * 3;
      setTimeout(() => {
        this.logger.debug(`[CubeService] 5→0: After ${partialTime} ms, switching Blog→Home & Contact→About`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 5→0: Final state confirmed as Home (0)`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }
    /* 5 nach 1 */
    else if (oldIndex === 5 && targetIndex === 1) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: false,
        blog: true,
        contact: true
      });
      const partialTime = (this.rotationDuration / steps) * 2;
      setTimeout(() => {
        this.logger.debug(`[CubeService] 5→1: After ${partialTime} ms, switching Blog→Home & Contact→About`);
        this.updateActiveFaces({
          home: false,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 5→1: Final state confirmed as About (1)`);
        this.updateActiveFaces({
          home: false,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 4 && targetIndex === 1) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: false,
        blog: true,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 500;
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→1: After ${partialTime} ms, switching Blog→Home & Contact→About`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→1: Final state confirmed as About (1)`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 4 && targetIndex === 0) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: false,
        blog: true,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 500;
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→0: After ${partialTime} ms, switching Blog→Home & Contact→About`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→0: Final state confirmed as Home (1)`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: false,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 2 && targetIndex === 5) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: true,
        about: true,
        resume: true,
        portfolio: false,
        blog: false,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 1500;
      this.logger.debug('[CubeService] PartialTime', partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 2→5: After ${partialTime} ms, switching Home→Blog & About→Contact`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 2→5: Final state confirmed as Contact (1)`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 1 && targetIndex === 2) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: true,
        about: true,
        resume: true,
        portfolio: false,
        blog: false,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 1500;
      this.logger.debug('[CubeService] PartialTime', partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 1→2: After ${partialTime} ms, switching Home→Blog & About→Contact`);
        this.updateActiveFaces({
          home: false,
          about: true,
          resume: true,
          portfolio: true,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 1→2: Final state confirmed as Contact (1)`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: true,
          portfolio: true,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 3 && targetIndex === 5) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: true,
        blog: false,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 2800;
      this.logger.debug('[CubeService] PartialTime', partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 3→5: After ${partialTime} ms, switching Home→Blog & About→Contact`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 3→5: Final state confirmed as Contact (1)`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: false,
          blog: true,
          contact: true
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 5 && targetIndex === 3) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: true,
        portfolio: false,
        blog: true,
        contact: true
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 1500;
      this.logger.debug('PartialTime', partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 5→3: After ${partialTime} ms, switching Blog→Home & Blog→Home`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: true,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 5→3: Final state confirmed as Portfolio (3)`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: true,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 4 && targetIndex === 3) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: false,
        blog: true,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 1500;
      this.logger.debug('[CubeService] PartialTime', partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→3: After ${partialTime} ms, switching Blog→Home & Contact→About`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: false,
          portfolio: true,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→3: Final state confirmed as Portfolio (3)`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: false,
          portfolio: true,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }
    else if (oldIndex === 4 && targetIndex === 2) {
      immediateSwitch = false;
      this.updateActiveFaces({
        home: false,
        about: false,
        resume: false,
        portfolio: false,
        blog: true,
        contact: false
      });
      const partialTime = (this.rotationDuration / steps) * 3 - 1500;
      this.logger.debug('[CubeService] PartialTime', partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→2: After ${partialTime} ms, switching Blog→Home & Contact→About`);
        this.updateActiveFaces({
          home: false,
          about: false,
          resume: true,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, partialTime);
      setTimeout(() => {
        this.logger.debug(`[CubeService] 4→2: Final state confirmed as Vita (2)`);
        this.updateActiveFaces({
          home: true,
          about: true,
          resume: true,
          portfolio: false,
          blog: false,
          contact: false
        });
      }, this.rotationDuration);
    }

    // Standardfall
if (immediateSwitch) {
const current = this._activeFaces.getValue(); // ✅

const keys = ['home','about','resume','portfolio','blog','contact'] as const;
const k = keys[targetIndex];

this.updateActiveFaces({
  ...current,
  [k]: true
});
}

    localStorage.setItem('cubeState', JSON.stringify({
      rotationY: this.rotationY,
      currentIndex: this.currentIndex
    }));
  }

  private animateRotation(deg: number) {
    const cube = document.querySelector('.box') as HTMLElement;
    const anim = cube.animate([
      { transform: `rotateY(${this.rotationY}deg)` },
      { transform: `rotateY(${this.rotationY + deg}deg)` }
    ], {
      duration: this.rotationDuration,
      easing: 'ease-in-out',
      fill: 'forwards'
    });

    anim.onfinish = () => {
      this.rotationY += deg;
      this.logger.debug('[CubeService] rotationY', this.rotationY);
    };
  }
}
